/**
 * R4.4 light/dark theme. <html data-theme="light|dark"> drives every colour
 * token (src/theme/tokens.css). public/index.html sets it BEFORE first paint
 * (no flash); this module keeps it in sync afterwards.
 * - A stored choice (localStorage "zaynic-theme") wins.
 * - No stored choice: follow the operating system, live.
 */
import { useEffect, useState } from 'react';

export const THEME_STORAGE_KEY = 'zaynic-theme';
const EVENT = 'zaynic:theme';

function readStored() {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return v === 'dark' || v === 'light' ? v : null;
  } catch {
    return null;
  }
}

function systemTheme() {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function resolveTheme() {
  return readStored() || systemTheme();
}

export function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  window.dispatchEvent(new CustomEvent(EVENT, { detail: theme }));
}

export function setTheme(theme) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* private mode: still switch for this page view */
  }
  applyTheme(theme);
}

export function currentTheme() {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

/** Call once at startup: follow OS changes while the user hasn't chosen. */
export function startThemeSync() {
  applyTheme(resolveTheme());
  try {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => { if (!readStored()) applyTheme(systemTheme()); };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  } catch {
    /* old browsers: no live follow */
  }
  window.addEventListener('storage', (e) => {
    if (e.key === THEME_STORAGE_KEY) applyTheme(resolveTheme());
  });
}

export function useTheme() {
  const [theme, setState] = useState(currentTheme);
  useEffect(() => {
    const on = (e) => setState(e.detail);
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  return [theme, (t) => setTheme(t)];
}
